/**
 * Single source of truth: each row is one allowed `model_name` (TYPE_MAP key) and
 * one UI option. `MODEL_KEYS` and `MODEL_OPTIONS` are derived so they always match.
 */
const MODEL_DEFS = Object.freeze([
  { id: "GPT_5_4", key: "gpt-5.4" },
  { id: "GPT_5_4_MINI", key: "gpt-5.4-mini" },
  { id: "GPT_5_4_NANO", key: "gpt-5.4-nano" },
  { id: "GPT_5_6_LUNA", key: "gpt-5.6-luna" },
  { id: "GPT_5_6_TERRA", key: "gpt-5.6-terra" },
  { id: "LLAMA_3_1_8B_INSTANT", key: "llama-3.1-8b-instant" },
  { id: "ANTHROPIC_CLAUDE_SONNET_4_5", key: "anthropic.claude-sonnet-4-5" },
  { id: "ANTHROPIC_CLAUDE_HAIKU_4_5", key: "anthropic.claude-haiku-4-5" },
  { id: "CEREBRAS_LLAMA3_1_8B", key: "cerebras.llama3.1-8b" },
  { id: "CEREBRAS_LLAMA_3_3_70B", key: "cerebras.llama-3.3-70b" },
  { id: "GEMINI_3_1_FLASH_LITE", key: "gemini-3.1-flash-lite" },
  { id: "GEMINI_3_1_PRO", key: "gemini-3.1-pro" },
  { id: "GEMINI_3_5_FLASH", key: "gemini-3.5-flash" },
  { id: "GEMINI_3_5_FLASH_LITE", key: "gemini-3.5-flash-lite" },
  { id: "GEMINI_3_6_FLASH", key: "gemini-3.6-flash" },
  { id: "DEEPSEEK_V3", key: "deepseek-v3" },
]);

/** @type {Record<string, string>} */
export const MODEL_KEYS = Object.freeze(Object.fromEntries(MODEL_DEFS.map((row) => [row.id, row.key])));

/** Dropdown rows: `key` is always the canonical string from `MODEL_DEFS`; label matches key. */
export const MODEL_OPTIONS = Object.freeze(
  MODEL_DEFS.map((row) => ({
    key: row.key,
    label: row.key,
  }))
);

export const DEFAULT_MODEL_KEY = MODEL_KEYS.GPT_5_4_MINI;

const MODEL_KEY_SET = new Set(MODEL_DEFS.map((row) => row.key));

/**
 * Returns a backend-safe model key. Unknown / blank input falls back to
 * `fallback` (or `DEFAULT_MODEL_KEY` if the fallback is also unknown).
 *
 * @param {string | null | undefined} value
 * @param {string} [fallback=DEFAULT_MODEL_KEY]
 * @returns {string}
 */
export function resolveModelKey(value, fallback = DEFAULT_MODEL_KEY) {
  const candidate = typeof value === "string" ? value.trim() : "";
  if (candidate && MODEL_KEY_SET.has(candidate)) {
    return candidate;
  }
  return MODEL_KEY_SET.has(fallback) ? fallback : DEFAULT_MODEL_KEY;
}

/**
 * Like `resolveModelKey` but returns `null` for unknown / blank input instead
 * of falling back. Useful when "no selection" means "let the backend pick its
 * per-agent default".
 *
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
export function resolveOptionalModelKey(value) {
  const candidate = typeof value === "string" ? value.trim() : "";
  if (!candidate) return null;
  return MODEL_KEY_SET.has(candidate) ? candidate : null;
}
